import {
	type PageBreadcrumbAncestor,
	PageBreadcrumbs,
	Tabs,
} from "@components";
import { useEffect, useState } from "react";
import { VEHICLE_TABS } from "@/features/vehicle/constants/vehicle-tabs.constant";
import { useVehicleDocumentDrawerHandler } from "@/features/vehicle/handlers/vehicle-document-drawer.handler";
import type { VehicleApiResponse } from "@/features/vehicle/types";
import type { VehicleDocument } from "@/features/vehicle/types/vehicle-document.types";
import { veiculo_status_visualizar } from "@/services/analytics/events";
import { AssignmentsCard } from "./assignments-card";
import { CharacteristicsCard } from "./characteristics-card";
import { DetailsHeader } from "./details-header";
import { ExpedientCard } from "./expedient-card";
import { OdometerTabContent } from "./odometer-tab-content";
import { PreventiveCard } from "./preventive-card";
import { TelemetryCard } from "./telemetry-card";
import { VehicleDocumentDrawer } from "./vehicle-document-drawer";
import { VehicleDocumentsSection } from "./vehicle-documents-history";
import { VehicleDocumentsMainTable } from "./vehicle-documents-main-table";
import { VehicleFormModal } from "./vehicle-form-modal";

const VEHICLE_BREADCRUMB_ANCESTORS: PageBreadcrumbAncestor[] = [
	{ label: "Veículos", href: "/vehicles" },
];

const BREADCRUMB_CURRENT_BY_TAB: Record<string, string> = {
	documentos: "Documentos",
	hodometro: "Hodômetro",
	telemetria: "Telemetria",
	expediente: "Expediente",
};

function getVehicleBreadcrumbCurrent(tabId: string): string {
	return BREADCRUMB_CURRENT_BY_TAB[tabId] ?? "Informações do veículo";
}

type VehicleDetailsContentProps = {
	vehicle?: VehicleApiResponse;
	isLoading?: boolean;
};

export const VehicleDetailsContent = ({
	vehicle: propVehicle,
	isLoading,
}: VehicleDetailsContentProps) => {
	const vehicle = propVehicle;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("dados");

	const {
		isOpen: drawerOpen,
		editingDocument,
		openForNew: handleAddDocument,
		openForEdit,
		close: handleCloseDrawer,
	} = useVehicleDocumentDrawerHandler();

	useEffect(() => {
		if (vehicle?.id) {
			veiculo_status_visualizar(vehicle.id);
		}
	}, [vehicle?.id]);

	if (isLoading) {
		return (
			<div className="flex min-h-screen w-full flex-col px-6 pt-8 pb-6">
				<PageBreadcrumbs
					ancestors={VEHICLE_BREADCRUMB_ANCESTORS}
					current="Informações do veículo"
				/>
				<div className="flex flex-1 flex-col items-center justify-center">
					<p className="text-fg-secondary">Carregando...</p>
				</div>
			</div>
		);
	}

	if (!vehicle) {
		return (
			<div className="flex min-h-screen w-full flex-col px-6 pt-8 pb-6">
				<PageBreadcrumbs
					ancestors={VEHICLE_BREADCRUMB_ANCESTORS}
					current="Veículo não encontrado"
				/>
				<div className="flex flex-1 flex-col items-center justify-center">
					<p className="text-fg-secondary">Veículo não encontrado.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen w-full flex-col pb-8">
			<PageBreadcrumbs
				ancestors={VEHICLE_BREADCRUMB_ANCESTORS}
				className="mx-6 mt-8"
				current={getVehicleBreadcrumbCurrent(activeTab)}
			/>

			<DetailsHeader
				bodyType={vehicle.bodyType}
				brand={vehicle.brand}
				brandValue={vehicle.brand}
				chassis={vehicle.chassisNumber}
				className="mx-6 mb-9"
				crlvDocumentId={vehicle.crlvDocumentId}
				model={vehicle.model}
				odometer={vehicle.odometer}
				odometerLastUpdatedAt={vehicle.odometerLastUpdatedAt}
				plate={vehicle.plate}
				status={vehicle.status}
				vehicleId={vehicle.id}
			/>

			<Tabs
				className="mb-5 overflow-x-auto px-6"
				defaultSelectedKey={activeTab}
				onSelectionChange={(tabId) =>
					setActiveTab(tabId as (typeof VEHICLE_TABS)[number]["id"])
				}
			>
				<Tabs.List
					className="w-fit rounded-lg"
					items={VEHICLE_TABS}
					type="button-border"
				>
					{(tab) => <Tabs.Item {...tab} />}
				</Tabs.List>
			</Tabs>

			{activeTab === "dados" && (
				<div className="mx-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
					<CharacteristicsCard
						onEditClick={() => setIsModalOpen(true)}
						vehicle={vehicle}
					/>
					<PreventiveCard vehicleId={String(vehicle.id)} />
					<AssignmentsCard
						bodyType={vehicle.bodyType}
						vehicleId={vehicle.id}
						vehiclePlate={vehicle.plate}
					/>
				</div>
			)}

			{activeTab === "documentos" && (
				<>
					<VehicleDocumentsMainTable
						onAdd={handleAddDocument}
						onEdit={openForEdit}
					/>

					<VehicleDocumentsSection
						editingDocument={editingDocument}
						isDrawerOpen={drawerOpen}
						onAdd={handleAddDocument}
						onCloseDrawer={handleCloseDrawer}
						onEdit={openForEdit}
						onSubmitDocument={(_doc: VehicleDocument) => {
							handleCloseDrawer();
						}}
					/>

					<VehicleDocumentDrawer
						documentId={editingDocument?.id ?? null}
						initialDocument={editingDocument}
						isOpen={drawerOpen}
						onClose={handleCloseDrawer}
						onSubmit={(_doc: VehicleDocument) => {
							handleCloseDrawer();
						}}
					/>
				</>
			)}

			{activeTab === "hodometro" && (
				<OdometerTabContent
					currentOdometer={vehicle.odometer}
					vehicleHeaderSubtitle={`${vehicle.plate} ${vehicle.brand ? `| ${vehicle.brand}` : ""} ${vehicle.model ? `- ${vehicle.model}` : ""}`}
					vehicleId={String(vehicle.id)}
				/>
			)}

			{activeTab === "expediente" && (
				<div className="mx-6">
					<ExpedientCard vehicleId={String(vehicle.id)} />
				</div>
			)}

			{activeTab === "telemetria" && (
				<div className="mx-6">
					<TelemetryCard
						telemetrySource={vehicle.telemetrySource}
						vehicleId={String(vehicle.id)}
					/>
				</div>
			)}

			{activeTab !== "dados" &&
				activeTab !== "documentos" &&
				activeTab !== "expediente" &&
				activeTab !== "telemetria" &&
				[
					"checklist",
					"manutencao",
					"financeiro",
					"atribuicao",
					"problemas",
				].includes(activeTab) && (
					<div className="min-h-[120px] rounded-[20px] border border-secondary_alt border-solid bg-bg-secondary p-6">
						<p className="text-fg-secondary text-sm">
							Conteúdo da aba{" "}
							{VEHICLE_TABS.find((t) => t.id === activeTab)?.label ?? activeTab}
							.
						</p>
					</div>
				)}

			<VehicleFormModal
				initialData={vehicle}
				isOpen={isModalOpen}
				mode="edit"
				onClose={() => setIsModalOpen(false)}
			/>
		</div>
	);
};
